import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Package, ArrowLeft, Plus } from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import { PageHeader, AppButton, AppModal } from "../ui";
import {
  ASSET_STATUSES, ASSET_STATUS_LABEL, ASSET_CONDITIONS,
  DEPRECIATION_METHODS,
} from "./assetConstants";
import "./assets.scss";

const blankForm = {
  name: "",
  categoryId: "",
  brand: "",
  model: "",
  serialNumber: "",
  vendorId: "",
  invoiceNumber: "",
  purchaseDate: "",
  purchaseCost: 0,
  currency: "INR",
  taxAmount: 0,
  warrantyMonths: 0,
  amcVendorId: "",
  amcExpiry: "",
  depreciationMethod: "none",
  usefulLifeMonths: 0,
  salvageValue: 0,
  depreciationRate: 0,
  status: "in-stock",
  condition: "good",
  location: "",
  branchId: "",
  description: "",
  notes: "",
  tagPrefix: "",
};

const AssetFormPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Quick-add category modal
  const blankCat = { name: "", code: "", description: "", defaultDepreciationMethod: "none", defaultUsefulLifeMonths: 0, defaultSalvagePercent: 0 };
  const [showCatModal, setShowCatModal] = useState(false);
  const [catForm, setCatForm] = useState(blankCat);
  const [catSaving, setCatSaving] = useState(false);

  // Quick-add vendor modal (shared for vendorId and amcVendorId)
  const blankVendor = { name: "", contactPerson: "", phone: "", email: "", gstin: "", address: "" };
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorForm, setVendorForm] = useState(blankVendor);
  const [vendorSaving, setVendorSaving] = useState(false);
  const [vendorTarget, setVendorTarget] = useState(null); // 'vendorId' | 'amcVendorId'

  useEffect(() => {
    let mounted = true;
    Promise.all([
      apiClient.get("/assets/categories").catch(() => ({ data: { categories: [] } })),
      apiClient.get("/assets/vendors").catch(() => ({ data: { vendors: [] } })),
      apiClient.get("/branches").catch(() => ({ data: { branches: [] } })),
    ]).then(([c, v, b]) => {
      if (!mounted) return;
      setCategories(c.data.categories || []);
      setVendors(v.data.vendors || []);
      setBranches(b.data.branches || []);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    apiClient.get(`/assets/${id}`).then(({ data }) => {
      const a = data.asset || {};
      setForm({
        ...blankForm,
        ...a,
        categoryId: a.categoryId?._id || a.categoryId || "",
        vendorId: a.vendorId?._id || a.vendorId || "",
        amcVendorId: a.amcVendorId?._id || a.amcVendorId || "",
        branchId: a.branchId?._id || a.branchId || "",
        purchaseDate: a.purchaseDate ? a.purchaseDate.slice(0, 10) : "",
        amcExpiry: a.amcExpiry ? a.amcExpiry.slice(0, 10) : "",
      });
      setLoading(false);
    }).catch(() => {
      setToast({ show: true, message: "Failed to load asset.", type: "error" });
      setLoading(false);
    });
  }, [id, isEdit]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const saveQuickCat = async () => {
    if (!catForm.name.trim()) return;
    setCatSaving(true);
    try {
      const { data } = await apiClient.post("/assets/categories", catForm);
      const newCat = data.category;
      setCategories(prev => [...prev, newCat].sort((a, b) => a.name.localeCompare(b.name)));
      update("categoryId", newCat._id);
      setShowCatModal(false);
      setCatForm(blankCat);
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to create category.", type: "error" });
    }
    setCatSaving(false);
  };

  const openVendorModal = (target) => { setVendorTarget(target); setVendorForm(blankVendor); setShowVendorModal(true); };

  const saveQuickVendor = async () => {
    if (!vendorForm.name.trim()) return;
    setVendorSaving(true);
    try {
      const { data } = await apiClient.post("/assets/vendors", vendorForm);
      const newVendor = data.vendor;
      setVendors(prev => [...prev, newVendor].sort((a, b) => a.name.localeCompare(b.name)));
      if (vendorTarget) update(vendorTarget, newVendor._id);
      setShowVendorModal(false);
      setVendorForm(blankVendor);
      setVendorTarget(null);
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to create vendor.", type: "error" });
    }
    setVendorSaving(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setToast({ show: true, message: "Name is required.", type: "error" });
      return;
    }
    if (!form.categoryId) {
      setToast({ show: true, message: "Category is required.", type: "error" });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Strip empty refs so we don't send empty strings as ObjectIds
      ['vendorId', 'amcVendorId', 'branchId'].forEach(k => { if (!payload[k]) payload[k] = null; });
      ['purchaseCost', 'taxAmount', 'warrantyMonths', 'usefulLifeMonths', 'salvageValue', 'depreciationRate']
        .forEach(k => { payload[k] = Number(payload[k]) || 0; });

      if (isEdit) {
        await apiClient.put(`/assets/${id}`, payload);
        setToast({ show: true, message: "Asset updated.", type: "success" });
        setTimeout(() => navigate(`/assets/${id}`), 600);
      } else {
        const { data } = await apiClient.post(`/assets`, payload);
        setToast({ show: true, message: "Asset created.", type: "success" });
        setTimeout(() => navigate(`/assets/${data.asset._id}`), 600);
      }
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Save failed.", type: "error" });
    }
    setSaving(false);
  };

  if (loading) return <div className="assets-page"><div className="assets-page__empty">Loading...</div></div>;

  return (
    <div className="assets-page">
      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      <PageHeader
        backButton
        onBack={() => navigate("/assets")}
        icon={Package}
        title={isEdit ? "Edit Asset" : "New Asset"}
        description={isEdit ? "Update asset details" : "Add a physical asset to inventory"}
      />

      <form className="asset-form" onSubmit={handleSubmit}>
        {/* ─── Basic Information ────────────────────────────────────────── */}
        <section className="asset-form__section">
          <h3 className="asset-form__section-title">Basic Information</h3>
          <div className="asset-form__grid">
            <Field label="Asset Name *">
              <input className="asset-form__input" value={form.name} onChange={e => update("name", e.target.value)} required />
            </Field>
            <Field label="Category *">
              <div className="asset-form__select-row">
                <select className="asset-form__select" value={form.categoryId} onChange={e => update("categoryId", e.target.value)} required>
                  <option value="">Select category…</option>
                  {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
                <AppButton type="button" variant="outline" size="sm" onClick={() => { setCatForm(blankCat); setShowCatModal(true); }}><Plus size={14} /></AppButton>
              </div>
            </Field>
            <Field label="Brand">
              <input className="asset-form__input" value={form.brand} onChange={e => update("brand", e.target.value)} />
            </Field>
            <Field label="Model">
              <input className="asset-form__input" value={form.model} onChange={e => update("model", e.target.value)} />
            </Field>
            <Field label="Serial Number">
              <input className="asset-form__input" value={form.serialNumber} onChange={e => update("serialNumber", e.target.value)} />
            </Field>
            {!isEdit && (
              <Field label="Tag Prefix" hint="Optional override; defaults to category code">
                <input className="asset-form__input" value={form.tagPrefix} onChange={e => update("tagPrefix", e.target.value.toUpperCase())} maxLength={6} />
              </Field>
            )}
            <Field label="Status">
              <select className="asset-form__select" value={form.status} onChange={e => update("status", e.target.value)}>
                {ASSET_STATUSES.map(s => <option key={s} value={s}>{ASSET_STATUS_LABEL[s]}</option>)}
              </select>
            </Field>
            <Field label="Condition">
              <select className="asset-form__select" value={form.condition} onChange={e => update("condition", e.target.value)}>
                {ASSET_CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Branch">
              <select className="asset-form__select" value={form.branchId || ""} onChange={e => update("branchId", e.target.value)}>
                <option value="">— None —</option>
                {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Location">
              <input className="asset-form__input" value={form.location} onChange={e => update("location", e.target.value)} placeholder="e.g. Lab 2, Rack A3" />
            </Field>
          </div>
        </section>

        {/* ─── Purchase & Vendor ────────────────────────────────────────── */}
        <section className="asset-form__section">
          <h3 className="asset-form__section-title">Purchase & Vendor</h3>
          <div className="asset-form__grid">
            <Field label="Vendor">
              <div className="asset-form__select-row">
                <select className="asset-form__select" value={form.vendorId || ""} onChange={e => update("vendorId", e.target.value)}>
                  <option value="">— None —</option>
                  {vendors.map(v => <option key={v._id} value={v._id}>{v.name}</option>)}
                </select>
                <AppButton type="button" variant="outline" size="sm" onClick={() => openVendorModal("vendorId")}><Plus size={14} /></AppButton>
              </div>
            </Field>
            <Field label="Invoice Number">
              <input className="asset-form__input" value={form.invoiceNumber} onChange={e => update("invoiceNumber", e.target.value)} />
            </Field>
            <Field label="Purchase Date">
              <input className="asset-form__input" type="date" value={form.purchaseDate || ""} onChange={e => update("purchaseDate", e.target.value)} />
            </Field>
            <Field label="Purchase Cost">
              <input className="asset-form__input" type="number" min="0" step="0.01" value={form.purchaseCost} onChange={e => update("purchaseCost", e.target.value)} />
            </Field>
            <Field label="Tax Amount">
              <input className="asset-form__input" type="number" min="0" step="0.01" value={form.taxAmount} onChange={e => update("taxAmount", e.target.value)} />
            </Field>
            <Field label="Currency">
              <input className="asset-form__input" value={form.currency} onChange={e => update("currency", e.target.value.toUpperCase())} maxLength={3} />
            </Field>
          </div>
        </section>

        {/* ─── Warranty & AMC ───────────────────────────────────────────── */}
        <section className="asset-form__section">
          <h3 className="asset-form__section-title">Warranty & AMC</h3>
          <div className="asset-form__grid">
            <Field label="Warranty (months)">
              <input className="asset-form__input" type="number" min="0" value={form.warrantyMonths} onChange={e => update("warrantyMonths", e.target.value)} />
            </Field>
            <Field label="AMC Vendor">
              <div className="asset-form__select-row">
                <select className="asset-form__select" value={form.amcVendorId || ""} onChange={e => update("amcVendorId", e.target.value)}>
                  <option value="">— None —</option>
                  {vendors.map(v => <option key={v._id} value={v._id}>{v.name}</option>)}
                </select>
                <AppButton type="button" variant="outline" size="sm" onClick={() => openVendorModal("amcVendorId")}><Plus size={14} /></AppButton>
              </div>
            </Field>
            <Field label="AMC Expiry">
              <input className="asset-form__input" type="date" value={form.amcExpiry || ""} onChange={e => update("amcExpiry", e.target.value)} />
            </Field>
          </div>
        </section>

        {/* ─── Depreciation ─────────────────────────────────────────────── */}
        <section className="asset-form__section">
          <h3 className="asset-form__section-title">Depreciation</h3>
          <div className="asset-form__grid">
            <Field label="Method">
              <select className="asset-form__select" value={form.depreciationMethod} onChange={e => update("depreciationMethod", e.target.value)}>
                {DEPRECIATION_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
            <Field label="Useful Life (months)" hint="Used by Straight-Line">
              <input className="asset-form__input" type="number" min="0" value={form.usefulLifeMonths} onChange={e => update("usefulLifeMonths", e.target.value)} />
            </Field>
            <Field label="Salvage Value">
              <input className="asset-form__input" type="number" min="0" value={form.salvageValue} onChange={e => update("salvageValue", e.target.value)} />
            </Field>
            <Field label="Depreciation Rate (%/yr)" hint="Used by WDV">
              <input className="asset-form__input" type="number" min="0" max="100" step="0.01" value={form.depreciationRate} onChange={e => update("depreciationRate", e.target.value)} />
            </Field>
          </div>
        </section>

        {/* ─── Notes ────────────────────────────────────────────────────── */}
        <section className="asset-form__section">
          <h3 className="asset-form__section-title">Description & Notes</h3>
          <div className="asset-form__grid">
            <Field label="Description">
              <textarea className="asset-form__textarea" value={form.description} onChange={e => update("description", e.target.value)} />
            </Field>
            <Field label="Internal Notes">
              <textarea className="asset-form__textarea" value={form.notes} onChange={e => update("notes", e.target.value)} />
            </Field>
          </div>
        </section>

        <div className="asset-form__actions">
          <AppButton type="button" variant="secondary" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} /> Cancel
          </AppButton>
          <AppButton type="submit" disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : (isEdit ? "Update Asset" : "Create Asset")}
          </AppButton>
        </div>
      </form>

      {/* ─── Quick-add Category Modal ─────────────────────────────────── */}
      <AppModal show={showCatModal} onHide={() => setShowCatModal(false)} title="New Category" size="sm">
        <div>
          <div className="assets-modal__field">
            <label className="assets-modal__label">Name *</label>
            <input className="assets-modal__input" value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} autoFocus />
          </div>
          <div className="assets-modal__field">
            <label className="assets-modal__label">Code (e.g. LAP, BRD)</label>
            <input className="assets-modal__input" value={catForm.code} onChange={e => setCatForm({ ...catForm, code: e.target.value.toUpperCase() })} maxLength={6} />
          </div>
          <div className="assets-modal__field">
            <label className="assets-modal__label">Default Depreciation</label>
            <select className="assets-modal__select" value={catForm.defaultDepreciationMethod} onChange={e => setCatForm({ ...catForm, defaultDepreciationMethod: e.target.value })}>
              {DEPRECIATION_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div className="asset-form__actions">
            <AppButton type="button" variant="secondary" onClick={() => setShowCatModal(false)}><ArrowLeft size={14} /> Cancel</AppButton>
            <AppButton type="button" onClick={saveQuickCat} disabled={catSaving || !catForm.name.trim()}>
              <Plus size={14} /> {catSaving ? "Saving…" : "Add Category"}
            </AppButton>
          </div>
        </div>
      </AppModal>

      {/* ─── Quick-add Vendor Modal ───────────────────────────────────── */}
      <AppModal show={showVendorModal} onHide={() => setShowVendorModal(false)} title="New Vendor" size="sm">
        <div>
          {[["name", "Name *"], ["contactPerson", "Contact Person"], ["phone", "Phone"], ["email", "Email"], ["gstin", "GSTIN"]].map(([key, label]) => (
            <div className="assets-modal__field" key={key}>
              <label className="assets-modal__label">{label}</label>
              <input className="assets-modal__input" value={vendorForm[key] || ""} onChange={e => setVendorForm({ ...vendorForm, [key]: e.target.value })} autoFocus={key === "name"} />
            </div>
          ))}
          <div className="asset-form__actions">
            <AppButton type="button" variant="secondary" onClick={() => setShowVendorModal(false)}><ArrowLeft size={14} /> Cancel</AppButton>
            <AppButton type="button" onClick={saveQuickVendor} disabled={vendorSaving || !vendorForm.name.trim()}>
              <Plus size={14} /> {vendorSaving ? "Saving…" : "Add Vendor"}
            </AppButton>
          </div>
        </div>
      </AppModal>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div className="asset-form__field">
    <label className="asset-form__label">{label}</label>
    {children}
    {hint && <span className="asset-form__hint">{hint}</span>}
  </div>
);

export default AssetFormPage;
